import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Media } from "@/services/bedrock";
import ReactPlayer from "react-player";
import { Image } from "semantic-ui-react";

import { Spinner } from "@/components/ui";

const SEEK_READY_EPSILON_SECONDS = 0.4;
const BUFFERING_OVERLAY_DELAY_MS = 150;

export interface MediaPlayerHandle {
  seekTo: (seconds: number) => void;
}

interface Props {
  media: Media;
  previewOnly?: boolean;
  autoPlay?: boolean;
  playbackRate?: number;
  playing?: boolean;
  controls?: boolean;
  objectFit?: "contain" | "cover";
  onPlayingChange?: (playing: boolean) => void;
  onProgress?: (state: { playedSeconds: number; played: number }) => void;
  onDuration?: (duration: number) => void;
}

export const MediaPlayer = React.forwardRef<MediaPlayerHandle, Props>(
  (
    {
      media,
      previewOnly = false,
      autoPlay = false,
      playbackRate = 1,
      playing,
      controls = true,
      objectFit,
      onPlayingChange,
      onProgress,
      onDuration,
    },
    ref
  ) => {
    const playerRef = useRef<ReactPlayer | null>(null);
    const videoElRef = useRef<HTMLMediaElement | null>(null);
    const pendingSeekRef = useRef<number | null>(null);
    const isBufferingRef = useRef(false);
    const overlayTimerRef = useRef<number | null>(null);
    const [playWhenReady, setPlayWhenReady] = useState(autoPlay);
    const [mediaPlaying, setMediaPlaying] = useState(autoPlay);
    const [mediaReady, setMediaReady] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [showBufferingOverlay, setShowBufferingOverlay] = useState(false);
    const isControlled = playing !== undefined;
    const isPlaying = isControlled ? playing : mediaPlaying;

    const endBuffering = useCallback(() => {
      pendingSeekRef.current = null;
      isBufferingRef.current = false;
      if (overlayTimerRef.current != null) {
        window.clearTimeout(overlayTimerRef.current);
        overlayTimerRef.current = null;
      }
      setIsBuffering(false);
      setShowBufferingOverlay(false);
    }, []);

    const beginBuffering = useCallback(() => {
      if (!isBufferingRef.current) {
        isBufferingRef.current = true;
        setIsBuffering(true);
        overlayTimerRef.current = window.setTimeout(() => {
          setShowBufferingOverlay(true);
        }, BUFFERING_OVERLAY_DELAY_MS);
      }
    }, []);

    const handleNativeSeeking = useCallback(() => {
      const video = videoElRef.current;
      if (video && Number.isFinite(video.currentTime)) {
        pendingSeekRef.current = video.currentTime;
      }
      beginBuffering();
    }, [beginBuffering]);

    const handleCanPlay = useCallback(() => {
      const video = videoElRef.current;
      if (!isBufferingRef.current || video?.seeking) return;
      const pending = pendingSeekRef.current;
      if (pending != null && video && Math.abs(video.currentTime - pending) > SEEK_READY_EPSILON_SECONDS) {
        return;
      }
      pendingSeekRef.current = null;
      endBuffering();
    }, [endBuffering]);

    const detachVideoListeners = useCallback(() => {
      const video = videoElRef.current;
      if (!video) return;
      video.removeEventListener("seeking", handleNativeSeeking);
      video.removeEventListener("canplay", handleCanPlay);
      videoElRef.current = null;
    }, [handleCanPlay, handleNativeSeeking]);

    useImperativeHandle(ref, () => ({
      seekTo: (seconds: number) => {
        pendingSeekRef.current = seconds;
        beginBuffering();
        playerRef.current?.seekTo(seconds, "seconds");
      },
    }));

    const mediaKey = `${media?.id ?? ""}:${media?.uri ?? ""}`;
    const prevMediaKeyRef = useRef(mediaKey);
    useEffect(() => {
      if (prevMediaKeyRef.current === mediaKey) return;
      prevMediaKeyRef.current = mediaKey;
      endBuffering();
    }, [endBuffering, mediaKey]);

    useEffect(
      () => () => {
        detachVideoListeners();
        if (overlayTimerRef.current != null) window.clearTimeout(overlayTimerRef.current);
      },
      [detachVideoListeners]
    );

    useEffect(() => {
      if (autoPlay) {
        setPlayWhenReady(true);
        if (!isControlled) setMediaPlaying(true);
      }
    }, [autoPlay, isControlled]);

    useEffect(() => {
      if (!isControlled && playWhenReady && mediaReady) {
        setMediaPlaying(true);
      }
    }, [isControlled, playWhenReady, mediaReady]);

    const setPlaying = (next: boolean) => {
      if (!isControlled) setMediaPlaying(next);
      onPlayingChange?.(next);
    };

    const renderThumbnail = () => {
      if (media?.thumbnailUri) {
        return <Image src={media.thumbnailUri} className="mediaThumbnail" />;
      }

      if (media?.category === "VIDEO" && media?.uri) {
        return (
          <video
            src={`${media.uri}#t=0.001`}
            preload="metadata"
            className="mediaThumbnail"
            style={{ width: "100%", height: "auto", display: "block" }}
            muted
            playsInline
          />
        );
      }

      return null;
    };

    return (
      <div className="mediaContainer relative">
        {media?.category === "VIDEO" && !previewOnly && (
          <ReactPlayer
            ref={playerRef}
            url={media?.uri}
            light={autoPlay || isControlled ? false : renderThumbnail() ?? true}
            width="100%"
            height={objectFit === "contain" ? "100%" : "auto"}
            controls={controls}
            playsinline
            config={
              objectFit ? { file: { attributes: { style: { width: "100%", height: "100%", objectFit } } } } : undefined
            }
            playing={isPlaying && !isBuffering}
            playbackRate={playbackRate}
            onClickPreview={() => {
              setPlayWhenReady(true);
              setPlaying(true);
            }}
            onReady={player => {
              setMediaReady(true);
              detachVideoListeners();
              const video = player.getInternalPlayer();
              if (video instanceof HTMLMediaElement) {
                videoElRef.current = video;
                video.addEventListener("seeking", handleNativeSeeking);
                video.addEventListener("canplay", handleCanPlay);
              }
            }}
            onPlay={() => {
              if (isBufferingRef.current) return;
              setPlaying(true);
            }}
            onPause={() => {
              if (isBufferingRef.current) return;
              setPlaying(false);
            }}
            onBuffer={beginBuffering}
            onBufferEnd={() => {
              if (pendingSeekRef.current != null) return;
              endBuffering();
            }}
            onSeek={() => {
              const video = videoElRef.current ?? playerRef.current?.getInternalPlayer();
              if (
                video instanceof HTMLMediaElement &&
                (video.seeking || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA)
              ) {
                return;
              }
              pendingSeekRef.current = null;
              endBuffering();
            }}
            onProgress={state => {
              const pending = pendingSeekRef.current;
              if (pending != null && Math.abs(state.playedSeconds - pending) > SEEK_READY_EPSILON_SECONDS) {
                return;
              }
              onProgress?.({ playedSeconds: state.playedSeconds, played: state.played });
            }}
            onDuration={value => onDuration?.(value)}
            progressInterval={200}
          />
        )}
        {showBufferingOverlay && media?.category === "VIDEO" && !previewOnly ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-black/45 pointer-events-none"
            role="status"
            aria-live="polite"
          >
            <Spinner className="size-10 text-white" aria-label="Buffering" />
          </div>
        ) : null}

        {media?.category === "VIDEO" && previewOnly && renderThumbnail()}

        {media?.category === "IMAGE" && <Image src={media?.thumbnailUri || media?.uri} className="mediaThumbnail" />}
      </div>
    );
  }
);

MediaPlayer.displayName = "MediaPlayer";
